import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Avatar
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import Pagination from "@material-ui/lab/Pagination";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import MessageIcon from "@mui/icons-material/Message";
import SpeedIcon from "@mui/icons-material/Speed";
import MenuIcon from "@mui/icons-material/Menu";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";
import PublicIcon from "@mui/icons-material/Public";
import PersonIcon from "@mui/icons-material/Person";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_QUICKMESSAGES":
      return action.payload;
    case "UPDATE_QUICKMESSAGES": {
      const quickemessage = action.payload;
      const idx = state.findIndex((u) => u.id === quickemessage.id);
      if (idx !== -1) {
        const newState = [...state];
        newState[idx] = quickemessage;
        return newState;
      }
      return [quickemessage, ...state];
    }
    case "DELETE_QUICKMESSAGE": {
      const quickemessageId = action.payload;
      return state.filter((u) => u.id !== quickemessageId);
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// QuickMessages
// ---------------------------------------------------------------------------
const QuickMessages = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const { user } = useContext(AuthContext);
  const { profile } = user;

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  const fetchQuickemessages = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/quick-messages", {
        params: {
          searchParam,
          companyId: user.companyId,
          userId: user.id
        }
      });

      const allRecords = data.records || data || [];
      const itemsPerPage = 10;
      const total = allRecords.length;

      const startIndex = (pageNumber - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedRecords = allRecords.slice(startIndex, endIndex);

      dispatch({ type: "LOAD_QUICKMESSAGES", payload: paginatedRecords });
      setTotalCount(total);
      setTotalPages(Math.ceil(total / itemsPerPage));
    } catch (err) {
      console.error("Error fetching quick messages:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [searchParam, pageNumber, user.companyId, user.id]);

  useEffect(() => {
    setPageNumber(1);
    dispatch({ type: "RESET" });
  }, [searchParam]);

  useEffect(() => {
    const delay = setTimeout(() => { fetchQuickemessages(); }, 500);
    return () => clearTimeout(delay);
  }, [fetchQuickemessages]);

  // ---------------------------------------------------------------------------
  // Socket
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketConnection({ companyId, userId: user.id });

    socket.on(`company${companyId}-quickemessage`, (data) => {
      console.log("Socket event received:", data);
      if (data.action === "update" || data.action === "create" || data.action === "delete") {
        fetchQuickemessages();
      }
    });

    return () => socket.disconnect();
  }, [user.companyId, user.id, fetchQuickemessages]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = useCallback(() => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  }, [fetchQuickemessages]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPageNumber(1);
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
      fetchQuickemessages();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingQuickemessage(null);
      setConfirmModalOpen(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPageNumber(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getTypeChip = (isCategory) => {
    if (isCategory) {
      return (
        <Chip
          icon={<MenuIcon sx={{ fontSize: 14 }} />}
          label="Menu"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            bgcolor: "rgba(59, 130, 246, 0.1)",
            color: "#3b82f6",
            border: "1px solid rgba(59, 130, 246, 0.2)"
          }}
        />
      );
    }
    return (
      <Chip
        icon={<SpeedIcon sx={{ fontSize: 14 }} />}
        label="Atalho"
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          bgcolor: "rgba(16, 185, 129, 0.1)",
          color: "#10b981",
          border: "1px solid rgba(16, 185, 129, 0.2)"
        }}
      />
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <MessageIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("quickMessages.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                {totalCount} mensagen{totalCount !== 1 ? "s" : ""} cadastrada{totalCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            {/* Search */}
            <TextField
              placeholder={i18n.t("quickMessages.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: 200, sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                  fontSize: "0.875rem",
                  "& fieldset": { borderColor: "divider" },
                  "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                  "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                }
              }}
            />

            {/* Add button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenQuickMessageDialog}
              sx={{
                bgcolor: PRIMARY_MAIN,
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                textTransform: "none",
                "&:hover": {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                }
              }}
            >
              {i18n.t("quickMessages.buttons.add")}
            </Button>
          </Stack>
        </Stack>

        {/* Tabela */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Cabeçalho da tabela */}
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              {searchParam
                ? `${totalCount} resultado${totalCount !== 1 ? "s" : ""}`
                : "Todas as mensagens rápidas"}
            </Typography>
          </Box>

          {/* Grid */}
          <TableContainer sx={{ maxHeight: "58vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("quickMessages.table.shortcode")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    Tipo
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("quickMessages.table.mediaName")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("quickMessages.table.status")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("quickMessages.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && <TableRowSkeleton columns={5} />}

                {!loading && quickemessages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <MessageIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
                          {i18n.t("quickMessages.noMessages") || "Nenhuma mensagem rápida encontrada"}
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.875rem" }}>
                          Use a busca ou crie sua primeira mensagem rápida
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && quickemessages.map((quickemessage) => (
                  <TableRow
                    key={quickemessage.id}
                    sx={{
                      "&:hover": {
                        bgcolor: "action.hover",
                        transition: "background-color 0.2s"
                      }
                    }}
                  >
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} sx={{ color: PRIMARY_MAIN, fontSize: "0.875rem" }}>
                        /{quickemessage.shortcode}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getTypeChip(quickemessage.isCategory)}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center">
                        {quickemessage.mediaName ? (
                          <>
                            <AttachFileIcon sx={{ mr: 0.75, color: "#10b981", fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: "#10b981", fontWeight: 500, fontSize: "0.875rem" }}>
                              {quickemessage.mediaName}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.disabled", fontSize: "0.875rem" }}>
                            {i18n.t("quickMessages.noAttachment") || "Sem anexo"}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {quickemessage.geral === true ? (
                        <Chip
                          icon={<PublicIcon sx={{ fontSize: 14 }} />}
                          label="Global"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: "rgba(16, 185, 129, 0.1)",
                            color: "#10b981",
                            border: "1px solid rgba(16, 185, 129, 0.2)"
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<PersonIcon sx={{ fontSize: 14 }} />}
                          label="Pessoal"
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: "rgba(148, 163, 184, 0.1)",
                            color: "text.secondary",
                            border: "1px solid rgba(148, 163, 184, 0.2)"
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {(profile === "admin" || profile === "supervisor" ||
                          (profile === "user" && !quickemessage.geral)) && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditQuickemessage(quickemessage)}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { color: PRIMARY_MAIN, bgcolor: "rgba(25, 118, 210, 0.08)" }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}

                        {(profile === "admin" || profile === "supervisor" ||
                          (profile === "user" && !quickemessage.geral)) && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingQuickemessage(quickemessage);
                            }}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.08)" }
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer — Paginação */}
          {!loading && quickemessages.length > 0 && (
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2.5,
                gap: 3,
                flexWrap: "wrap"
              }}
            >
              <Typography sx={{ color: "text.secondary", fontSize: "0.875rem", fontWeight: 500 }}>
                {searchParam
                  ? `Encontradas ${totalCount} mensagens`
                  : `Mostrando ${quickemessages.length} de ${totalCount} mensagens`}
              </Typography>

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={pageNumber}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      "&.Mui-selected": {
                        bgcolor: PRIMARY_MAIN,
                        color: "#fff",
                        "&:hover": {
                          bgcolor: PRIMARY_DARK
                        }
                      }
                    }
                  }}
                />
              )}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Modais */}
      <ConfirmationModal
        title={
          deletingQuickemessage
            ? `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`
            : ""
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          dispatch({ type: "RESET" });
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />
    </Box>
  );
};

export default QuickMessages;